import React, { useMemo, useState } from "react";

import Button from "./Button";
import Checkbox from "./Checkbox";
import Icon from "./Icon";
import Modal from "./Modal";
import Popover from "./Popover";
import SearchInput from "./SearchInput";
import useBreakpoint from "../../hooks/useBreakpoint";
import "./ScopeSelect.scss";

// Fontaine M54C — ScopeSelect: the filter-bar dropdown Manage Listings already
// used inline (its `.rc-listings__scope-*` block), lifted into a primitive so
// Buy Alerts and Leads render the exact same control instead of three
// look-alikes. One trigger naming whatever is selected, one panel of checkbox
// rows — cheaper on the row than N switches, and the current scope stays
// readable without opening anything.
//
// `multiple` (default) = independent flags, any combination, none = everything,
// rendered as checkbox rows.
//
// Single mode is a LISTBOX, not checkboxes. The payload behind these options takes one value
// (a sort key, a region, a status), so a column of tick-boxes promised a combination the
// control cannot express — "Last Created" and "Turnover: High To Low" are not both selectable.
// Rows are `role="option"` with the selected one marked and ticked on the right, the same
// idiom the store switcher uses. Labels start on the panel's left edge because there is no
// box gutter to clear. Re-picking the selected row still clears it.
//
// Options may also come grouped — `{ group: "Quote", options: [...] }` — for a
// vocabulary long enough that the headings are what make it readable (Orders'
// Request / Quote statuses). Flat and grouped entries can be mixed.
//
// ── Phone: a bottom sheet, not a floating panel ─────────────────────────────
// A panel anchored to its trigger is a different size and a different position
// for every one of these filters, and on a 390px screen it lands over the very
// rows the filter is about. On phones the options open in the app's existing
// bottom sheet (Modal variant="sheet") instead: one place, one size, capped at
// two thirds of the screen and scrolling inside itself.
//
// In `multiple` mode the sheet stages the selection and commits it on APPLY —
// a phone list that re-queried on every tick would reload the page under the
// user's thumb between two ticks of the same question. Single mode has nothing
// to stage: the pick IS the answer, so it applies and closes with no button.
//
// @param {string} placeholder            trigger label when nothing is selected
// @param {({key: string, label: string}|{group: string, options: []})[]} options
// @param {string[]|string|null} value    selected keys (multiple) / key (single)
// @param {(next) => void} onChange       next array (multiple) / key|null (single)
// @param {boolean} [searchable]          filter field above the sheet's list — for a
//                                        vocabulary too long to scroll (~200 countries)
// @param {(picked: {key, label}[]) => string} [summary]
//                                        trigger label for a selection, when naming every
//                                        picked option would not fit (40 countries)
export default function ScopeSelect({
  placeholder,
  options,
  value,
  onChange,
  multiple = true,
  label,
  className = "",
  size = "lg",
  searchable = false,
  summary,
}) {
  const { isPhone } = useBreakpoint();
  const [sheetOpen, setSheetOpen] = useState(false);
  // What the sheet is showing while it is open, in `multiple` mode. Null means
  // "not staging" — every other path reads the committed `value`.
  const [staged, setStaged] = useState(null);
  const [query, setQuery] = useState("");

  // One flat list for the label and the lookups; the groups only affect rendering.
  const flat = options.flatMap((o) => (o.options ? o.options : [o]));
  const committed = multiple ? (value ?? []) : value == null ? [] : [value];
  const selected = staged ?? committed;
  const isOn = (key) => selected.includes(key);

  const toggle = (key, next) => {
    if (!multiple) {
      onChange(next ? key : null);
      // The pick is the whole answer on a phone, so the sheet has done its job.
      setSheetOpen(false);
      return;
    }
    const nextKeys = next ? [...selected, key] : selected.filter((k) => k !== key);
    if (staged) setStaged(nextKeys);
    else onChange(nextKeys);
  };

  // One row. Multiple = a flag you can combine; single = one choice out of the list.
  const renderOption = (o) =>
    multiple ? (
      <Checkbox key={o.key} label={o.label} checked={isOn(o.key)} onChange={(next) => toggle(o.key, next)} />
    ) : (
      <button
        key={o.key}
        type="button"
        role="option"
        aria-selected={isOn(o.key)}
        className={`rc-scope-option${isOn(o.key) ? " is-selected" : ""}`}
        onClick={() => toggle(o.key, !isOn(o.key))}
      >
        <span className="rc-scope-option__label">{o.label}</span>
        {isOn(o.key) ? <Icon name="Check" size="xs" /> : null}
      </button>
    );

  // Filtering keeps the group structure: a heading with nothing left under it
  // is dropped rather than left standing over an empty space.
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;

    const hit = (o) => String(o.label).toLowerCase().includes(needle);

    return options
      .map((o) => (o.options ? { ...o, options: o.options.filter(hit) } : o))
      .filter((o) => (o.options ? o.options.length > 0 : hit(o)));
  }, [options, query]);

  const list = (rows) => (
    <div
      className={`rc-scope-menu${multiple ? "" : " rc-scope-menu--single"}`}
      role={multiple ? undefined : "listbox"}
      aria-label={multiple ? undefined : label || placeholder}
    >
      {rows.map((o) =>
        o.options ? (
          <div key={o.group} className="rc-scope-group" role={multiple ? undefined : "group"} aria-label={o.group}>
            <span className="rc-scope-group__label">{o.group}</span>
            {o.options.map((child) => renderOption(child))}
          </div>
        ) : (
          renderOption(o)
        ),
      )}
    </div>
  );

  // The trigger names what is selected. `summary` is for the case where that
  // does not fit — "Germany (+49), France (+33), Italy (+39), …" is a filter
  // row's whole width spent on the first two of forty.
  const picked = flat.filter((o) => committed.includes(o.key));
  const triggerLabel =
    (picked.length ? (summary ? summary(picked) : picked.map((o) => o.label).join(", ")) : "") ||
    placeholder;

  // The panel's contents are the same in the popover and in the sheet — a
  // filter field for a long vocabulary, then the rows. Only the container
  // differs, which is the whole difference between the two surfaces.
  const panelBody = (
    <>
      {searchable ? (
        <div className="rc-scope-search">
          <SearchInput
            size="lg"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onSearch={setQuery}
            onClear={() => setQuery("")}
            debounce={0}
            aria-label={`Search ${label || placeholder}`}
            placeholder="Search"
          />
        </div>
      ) : null}

      {visible.length ? list(visible) : <p className="rc-scope-empty">No matches.</p>}
    </>
  );

  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size={size}
      className="rc-scope-trigger"
      trailingIcon={<Icon name="ChevronDown" size="xs" />}
    >
      <span className="rc-scope-label">{triggerLabel}</span>
    </Button>
  );

  if (isPhone) {
    const openSheet = () => {
      setQuery("");
      setStaged(multiple ? committed : null);
      setSheetOpen(true);
    };
    const closeSheet = () => {
      setSheetOpen(false);
      setStaged(null);
    };

    return (
      <>
        {React.cloneElement(trigger, {
          onClick: openSheet,
          "aria-haspopup": "dialog",
          "aria-expanded": sheetOpen,
        })}

        <Modal
          open={sheetOpen}
          onClose={closeSheet}
          variant="sheet"
          title={label || placeholder}
          className={`rc-scope-sheet ${className}`.trim()}
          footer={
            multiple ? (
              <Button
                type="button"
                variant="primary"
                size="lg"
                onClick={() => {
                  onChange(staged ?? committed);
                  closeSheet();
                }}
              >
                Apply
              </Button>
            ) : null
          }
        >
          {panelBody}
        </Modal>
      </>
    );
  }

  return (
    <Popover
      label={label || placeholder}
      className={`rc-scope-pop ${className}`}
      side="bottom"
      align="end"
      trapFocus
      trigger={trigger}
      onOpenChange={(next) => {
        // A stale filter string would hide most of the list the next time the
        // panel opens, with nothing on screen explaining why.
        if (!next) setQuery("");
      }}
    >
      {panelBody}
    </Popover>
  );
}
